import { useEffect, useState } from 'react'
import type { Quote_ } from 'src/api/db/apiQuote'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Input } from 'src/shared/components/ui/input'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { useRequestContext } from '../../../hooks/RequestContext'
import { withSyncedPaymentFields } from '../utils'

const DEFAULT_TERMS = 30

export const AdminPaymentFields = ({ quote }: { quote: Quote_ }) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { refetchQuote } = useRequestContext()
  const { updateQuote } = useQuoteApi({ skipListQuery: true })

  const [paymentTerms, setPaymentTerms] = useState(
    String(quote.payment_terms ?? DEFAULT_TERMS)
  )
  const [paymentReference, setPaymentReference] = useState(
    quote.payment_reference ?? ''
  )

  useEffect(() => {
    setPaymentTerms(String(quote.payment_terms ?? DEFAULT_TERMS))
    setPaymentReference(quote.payment_reference ?? '')
  }, [quote.id, quote.payment_terms, quote.payment_reference])

  const save = async (next: {
    payment_terms: number
    payment_reference: string
  }) => {
    const prevTerms = quote.payment_terms ?? DEFAULT_TERMS
    const prevReference = quote.payment_reference ?? ''
    if (
      next.payment_terms === prevTerms &&
      next.payment_reference === prevReference
    ) {
      return
    }

    const syncedInvoice = withSyncedPaymentFields(
      quote.invoice_data,
      next.payment_terms,
      next.payment_reference
    )

    try {
      const result = await updateQuote.mutateAsync({
        id: quote.id,
        payment_terms: next.payment_terms,
        payment_reference: next.payment_reference,
        ...(syncedInvoice ? { invoice_data: syncedInvoice } : {}),
      })
      if (result.error) throw result.error
      refetchQuote()
    } catch (error) {
      console.error('Failed to save payment fields', error)
      toast({
        variant: 'destructive',
        title: t(
          'Could not save payment details',
          'Kunde inte spara betalningsuppgifter'
        ),
      })
      setPaymentTerms(String(prevTerms))
      setPaymentReference(prevReference)
    }
  }

  const onTermsBlur = () => {
    const parsed = Number.parseInt(paymentTerms, 10)
    const days =
      Number.isFinite(parsed) && parsed >= 0 && parsed <= 365
        ? parsed
        : DEFAULT_TERMS
    setPaymentTerms(String(days))
    void save({
      payment_terms: days,
      payment_reference: paymentReference.trim(),
    })
  }

  const onReferenceBlur = () => {
    const trimmed = paymentReference.trim()
    setPaymentReference(trimmed)
    const parsed = Number.parseInt(paymentTerms, 10)
    const days =
      Number.isFinite(parsed) && parsed >= 0 && parsed <= 365
        ? parsed
        : (quote.payment_terms ?? DEFAULT_TERMS)
    void save({
      payment_terms: days,
      payment_reference: trimmed,
    })
  }

  return (
    <Row className="flex-wrap gap-x-12 gap-y-4">
      <Column className="gap-0.5 min-w-[10rem]">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('Payment terms (days)', 'Betalningsvillkor (dagar)')}
        </span>
        <Input
          type="number"
          min={0}
          max={365}
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
          onBlur={onTermsBlur}
          className="h-8 w-28"
          disabled={updateQuote.isPending}
        />
      </Column>
      <Column className="gap-0.5 min-w-[12rem] flex-1">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {t('Payment reference', 'Betalreferens')}
        </span>
        <Input
          value={paymentReference}
          onChange={(e) => setPaymentReference(e.target.value)}
          onBlur={onReferenceBlur}
          placeholder={t('OCR / reference', 'OCR / referens')}
          className="h-8 max-w-sm"
          disabled={updateQuote.isPending}
        />
      </Column>
    </Row>
  )
}
